/**
 * Trade: control, flow, and who gets paid.
 *
 * The month's work is three passes over sixteen nodes and one over the province
 * table, and it happens in the Income phase of the council — once a month of
 * simulated time, never in a tick. See `tradeturn.ts` for the order.
 *
 *   1. **Value.** Every owned province puts `TRADE_SHARE` of its gold wealth
 *      into the node it trades through, scaled by exactly the same development,
 *      unrest, siege and brigandage terms the tax half is scaled by. The two
 *      halves of a province's wealth are the same wealth; only their destination
 *      differs.
 *   2. **Control.** A share of the node, per crown, out of trade power — held
 *      provinces, merchants standing there, forts, ports, and hosts on the
 *      ground. Smoothed toward its target rather than snapped to it, which is
 *      what makes control a thing you contest over seasons.
 *   3. **Flow.** Nodes are resolved from the deepest inland outward. Each takes
 *      a retained share, splits it by control, and pushes the rest down its
 *      outgoing roads — split by how good each road is and by whoever is leaning
 *      on it with a merchant.
 *
 * The realm's total gold is conserved by this. Nothing here mints money: the
 * share taken out of province tax is exactly the share put into the nodes, and
 * every coin a node retains is paid to somebody or, in a node no crown has any
 * power in at all, taken by nobody. What trade does is *move* income across the
 * map, from whoever grew the grain to whoever owns the road it left on.
 */

import { provinceDef, PROVINCE_COUNT } from '../../data/provinces.ts';
import { yieldScale } from './holdings.ts';
import { rawPoints } from './armies.ts';
import {
  MAX_ARMY_POWER,
  MerchantMission,
  POWER_PER_ARMY_POINT,
  POWER_PER_FORT,
  POWER_PER_MERCHANT,
  POWER_PER_PORT,
  POWER_PER_VALUE,
  STEER_POWER_SHARE,
  STEER_PULL,
  TRADE_SHARE,
  createTradeNodeState,
  retentionOf,
  steerWeightOf,
  type Merchant,
  type TradeNode,
  type TradeState,
} from './tradetypes.ts';
import { buildTradeNodes, nodeIndexOf, type TradeLane } from './tradenodes.ts';
import type { CampaignState, ProvinceState } from './types.ts';

// ── Building ───────────────────────────────────────────────────────────────

/**
 * The realm's sea lanes, if there are any yet. The second `mapsea` seam.
 *
 * Lanes are a graph parallel to `links` — `CampaignState.lanes` — and they are
 * kept separate on purpose: `canMarch` gates on `neighbours`, which reads
 * `links`, so a lane in there would let armies march across open water. They are
 * not on this branch yet, so this reads the field defensively and returns none
 * until it exists, exactly as `portLevel` does below. When it lands, the Midsea
 * becomes a set of trade roads without a line of this file changing.
 */
function campaignLanes(c: CampaignState): TradeLane[] {
  const maybe = c as CampaignState & { lanes?: TradeLane[] };
  return Array.isArray(maybe.lanes) ? maybe.lanes : [];
}

/** An empty trade state, for the moment before the graph is grown. */
export function emptyTrade(): TradeState {
  return { nodes: [], states: [], merchants: [], nextMerchantId: 1 };
}

/**
 * Grow the graph onto a campaign and give every crown its opening control.
 *
 * Called once from `createCampaign`, after the provinces and the links exist.
 * The opening control pass runs at full drift: a campaign should not open with
 * sixteen ownerless markets that take four months to notice who is holding them.
 */
export function initTrade(c: CampaignState): void {
  const nodes = buildTradeNodes(c.links, c.provinces.length, campaignLanes(c));
  c.trade = {
    nodes,
    states: nodes.map(() => createTradeNodeState(c.nations.length)),
    merchants: [],
    nextMerchantId: 1,
  };
  recomputeLocalValue(c);
  recomputeControl(c, 1);
  /**
   * Resolve one month of flow without paying it, so the markets have an answer
   * before the first council ever sits.
   *
   * `projectedIncome` adds `tradeIncomeOf` — what the markets returned *last*
   * month — because an honest forecast would mean resolving the whole graph and
   * the difference is a rounding error next to a war. On turn one there is no
   * last month, so it reported the tax half alone: the opening screen read
   * `+49` and the second month read `+75`, a 35% jump with no event behind it,
   * on the first screen anybody sees. `uifix` caught it from the panel side.
   *
   * A dry run fills `value`, `retained` and `income` from the opening board
   * without crediting a single treasury, so the forecast is right immediately
   * and no gold is minted for a month nobody lived through. That is also exactly
   * what the number means to a player: what these markets pay in a normal month.
   */
  runFlow(c, false);
}

// ── The province → node index ──────────────────────────────────────────────

/**
 * Which node a province trades through.
 *
 * A derived index over `trade.nodes`, keyed on the node array itself in a
 * `WeakMap` and thrown away the moment that array is replaced — the same shape
 * `graph.ts` uses over `links`, for the same reason and with the same rule: it
 * is a pure function of state that is already hashed, so it is not observable.
 * The node graph is built once and never mutated, so this is built once too.
 */
const NODE_OF = new WeakMap<TradeNode[], number[]>();

export function tradeNodeOf(c: CampaignState, province: number): number {
  const nodes = c.trade?.nodes;
  if (!nodes || nodes.length === 0) return -1;
  let idx = NODE_OF.get(nodes);
  if (!idx || idx.length !== c.provinces.length) {
    idx = nodeIndexOf(nodes, c.provinces.length);
    NODE_OF.set(nodes, idx);
  }
  return idx[province] ?? -1;
}

export function tradeNode(c: CampaignState, node: number): TradeNode | undefined {
  return c.trade?.nodes[node];
}

export function nodeName(c: CampaignState, node: number): string {
  return c.trade?.nodes[node]?.name ?? 'nowhere';
}

/** A node nothing flows out of. Everything that reaches it is taken there. */
export function isTerminal(c: CampaignState, node: number): boolean {
  const n = c.trade?.nodes[node];
  return !!n && n.outgoing.length === 0;
}

// ── 1. Value ───────────────────────────────────────────────────────────────

/**
 * What one province puts into its node this month.
 *
 * This used to spell out the same expression `doIncome` uses for tax, kept
 * correct by nothing but a comment asking whoever edited one to remember the
 * other. That is not a mechanism, and it did not hold: there were four copies of
 * it in the end, one of them hand-written in `src/ui/income.ts` because the
 * simulation had no per-province reader for the UI to ask.
 *
 * That drift very nearly happened the week this was written. The expression was
 * spelled out here by hand, and the holdings ladder then multiplied the tax half
 * by a province's tier without this copy knowing tiers existed — so a Seat of the
 * Realm would have paid ×3.52 tax and ×1.0 trade, the ladder would have lifted
 * only two thirds of a province's wealth, and every price on it was set against
 * the whole. It calls `yieldScale` now, which is the one definition of what a
 * province's output is scaled by; this function is that number times the share.
 *
 * Masterless ground contributes nothing. It grows no surplus anybody has
 * organised, and crediting it would mint gold the tax half never minted.
 */
export function provinceTradeValue(c: CampaignState, p: number): number {
  const prov = c.provinces[p];
  if (!prov || prov.owner < 0) return 0;
  return provinceDef(p).wealth * TRADE_SHARE * yieldScale(c, p);
}

export function recomputeLocalValue(c: CampaignState): void {
  const t = c.trade;
  for (const st of t.states) st.local = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    const k = tradeNodeOf(c, p);
    if (k < 0) continue;
    t.states[k].local += provinceTradeValue(c, p);
  }
}

// ── 2. Control ─────────────────────────────────────────────────────────────

/**
 * The `mapsea` seam, read defensively.
 *
 * Ports are being built in a parallel worktree and are not here yet. When a port
 * level lands on `ProvinceState` this starts returning it and coastal control
 * starts answering to harbours without a line of this file changing. Until then
 * it is zero and costs one property read a month per province.
 */
function portLevel(prov: ProvinceState): number {
  const maybe = prov as ProvinceState & { port?: number };
  return typeof maybe.port === 'number' ? maybe.port : 0;
}

/**
 * Force projected onto each node, per crown, capped.
 *
 * One pass over the army list rather than a province scan: an army only counts
 * where it actually stands, so a host on the road is projecting nothing, which
 * is both correct and the reason `moveTo` is checked.
 *
 * The cap is per crown per node and it matters. Uncapped, one doomstack parked
 * on a market outweighs every province in it, and trade becomes a second
 * scoreboard for whoever has the biggest army — the one thing it must not be.
 */
function armyPowerByNode(c: CampaignState, nodeCount: number): number[][] {
  const out: number[][] = [];
  for (let k = 0; k < nodeCount; k++) out.push(new Array<number>(c.nations.length).fill(0));
  for (const a of c.armies) {
    if (a.moveTo >= 0) continue;
    if (a.owner < 0 || a.owner >= c.nations.length) continue;
    const k = tradeNodeOf(c, a.at);
    if (k < 0) continue;
    out[k][a.owner] += rawPoints(a) * POWER_PER_ARMY_POINT;
  }
  for (const row of out) {
    for (let n = 0; n < row.length; n++) if (row[n] > MAX_ARMY_POWER) row[n] = MAX_ARMY_POWER;
  }
  return out;
}

/** Merchants that are in place and working, bucketed by node. */
export function workingMerchantsByNode(c: CampaignState): Merchant[][] {
  const out: Merchant[][] = c.trade.nodes.map(() => []);
  for (const m of c.trade.merchants) {
    if (m.travel > 0) continue;
    if (m.node < 0 || m.node >= out.length) continue;
    out[m.node].push(m);
  }
  return out;
}

/**
 * Recompute every node's control share, drifting toward the board's answer.
 *
 * `drift` of 1 snaps — used once, when the campaign is built. Everything else
 * uses `CONTROL_DRIFT`, so a market town changing hands takes a season or two to
 * change who is paid by it, and the crown that lost it is still being paid in
 * the meantime. That lag is not friction for its own sake: it is what turns
 * control into something a player pushes on for months rather than a readout of
 * the province table with extra steps.
 */
export function recomputeControl(c: CampaignState, drift: number): void {
  const t = c.trade;
  const nn = c.nations.length;
  const armyPower = armyPowerByNode(c, t.nodes.length);
  const byNode = workingMerchantsByNode(c);
  const power = new Array<number>(nn);

  for (let k = 0; k < t.nodes.length; k++) {
    const node = t.nodes[k];
    const st = t.states[k];
    for (let n = 0; n < nn; n++) power[n] = armyPower[k][n];

    for (const p of node.provinces) {
      const prov = c.provinces[p];
      const owner = prov.owner;
      if (owner < 0 || owner >= nn) continue;
      power[owner] +=
        provinceTradeValue(c, p) * POWER_PER_VALUE +
        prov.fort * POWER_PER_FORT +
        portLevel(prov) * POWER_PER_PORT;
    }

    for (const m of byNode[k]) {
      if (m.nation < 0 || m.nation >= nn) continue;
      power[m.nation] +=
        POWER_PER_MERCHANT * (m.mission === MerchantMission.Collect ? 1 : STEER_POWER_SHARE);
    }

    let total = 0;
    for (let n = 0; n < nn; n++) {
      if (!c.nations[n].alive) power[n] = 0;
      total += power[n];
    }

    if (st.control.length !== nn) st.control = new Array<number>(nn).fill(0);
    for (let n = 0; n < nn; n++) {
      const target = total > 0 ? power[n] / total : 0;
      const next = st.control[n] + (target - st.control[n]) * drift;
      // `-0` hashes differently from `0` and `JSON.stringify` writes it as `0`,
      // so a campaign would round-trip perfectly and report a different hash.
      // See `clampOpinion` in `diplomacy.ts`; the same trap, the same fix.
      st.control[n] = next === 0 ? 0 : next;
    }
  }
}

// ── 3. Flow ────────────────────────────────────────────────────────────────

/**
 * Nodes in resolution order: deepest inland first.
 *
 * Every arrow runs from a deeper node to a shallower one, so walking depth
 * downward guarantees a node is resolved only after everything that feeds it —
 * which is a topological order, obtained by sorting sixteen integers instead of
 * by running Kahn's algorithm and then having to decide what to do about a cycle
 * that the construction makes impossible anyway. See `tradenodes.ts`.
 */
function resolutionOrder(t: TradeState): number[] {
  const order = t.nodes.map((n) => n.id);
  order.sort((a, b) => t.nodes[b].depth - t.nodes[a].depth || a - b);
  return order;
}

/**
 * Move the month's value through the graph and pay everybody.
 *
 * Returns the total collected, which the caller logs and the tests assert
 * against the total produced.
 */
export function runFlow(c: CampaignState, pay = true): number {
  const t = c.trade;
  const nn = c.nations.length;
  const inbound = new Array<number>(t.nodes.length).fill(0);
  const byNode = workingMerchantsByNode(c);
  let paid = 0;

  for (const k of resolutionOrder(t)) {
    const node = t.nodes[k];
    const st = t.states[k];
    st.value = st.local + inbound[k];

    let controlled = 0;
    for (let n = 0; n < nn; n++) controlled += st.control[n];

    // The two shares, stored rather than recomputed: the flow needs them here,
    // the council needs them to decide whether a merchant is worth sending, and
    // the panel needs them to say *why* a market is leaking. Three readers, one
    // implementation of the rule.
    let collecting = 0;
    let steering = 0;
    for (const m of byNode[k]) {
      const share = st.control[m.nation] ?? 0;
      if (m.mission === MerchantMission.Collect) collecting += share;
      else steering += steerWeightOf(share);
    }
    st.collectShare = Math.min(1, collecting);
    st.steerShare = Math.min(1, steering);

    const terminal = node.outgoing.length === 0;
    /**
     * A node nobody has any power in retains nothing — its value runs straight
     * through to the next market, and at a terminal it is simply never collected
     * by anybody. That is the honest answer for a masterless port, and it is a
     * standing argument for taking the ground: the wealth is there and nobody is
     * picking it up.
     *
     * Everywhere else, `retentionOf` is the one rule: collectors hold value at
     * home, steerers push it out of the door, and a market with both is being
     * fought over. See `retainFloorWith` in `tradetypes.ts` for why the draining
     * half exists at all — without it, steering was worth about a fifth of what
     * collecting was worth in the same square and no crown ever chose it.
     */
    st.retained = controlled <= 0 ? 0 : terminal ? 1 : retentionOf(st.collectShare, st.steerShare);

    const kept = st.value * st.retained;
    if (st.income.length !== nn) st.income = new Array<number>(nn).fill(0);
    for (let n = 0; n < nn; n++) {
      // A crown that has gone under is paid nothing and is *shown* nothing.
      // `recomputeControl` zeroes a dead nation's power, but control only drifts
      // toward that over a season, so without this the panel would spend a few
      // months reporting a takings for a crown that no longer exists.
      const cut = c.nations[n].alive ? kept * st.control[n] : 0;
      st.income[n] = cut === 0 ? 0 : cut;
      if (cut > 0) {
        if (pay) c.nations[n].treasury.gold += cut;
        paid += cut;
      }
    }

    // What is left goes down the roads, split by how good each road is and by
    // whoever is leaning on it. A steerer's pull is his own weight plus whatever
    // control his crown has here — so holding the node steers it harder, but a
    // crown holding none of it can still reach in and pull. See
    // `STEER_MERCHANT_WEIGHT`.
    const leaving = st.value - kept;
    const weights: number[] = node.outWeight.slice();
    for (const m of byNode[k]) {
      if (m.mission !== MerchantMission.Steer) continue;
      const i = node.outgoing.indexOf(m.toward);
      if (i < 0) continue;
      weights[i] += node.outWeight[i] * STEER_PULL * steerWeightOf(st.control[m.nation] ?? 0);
    }
    let totalWeight = 0;
    for (const w of weights) totalWeight += w;
    st.outflow = new Array<number>(node.outgoing.length).fill(0);
    if (leaving > 0 && totalWeight > 0) {
      for (let i = 0; i < node.outgoing.length; i++) {
        const share = (leaving * weights[i]) / totalWeight;
        st.outflow[i] = share;
        inbound[node.outgoing[i]] += share;
      }
    }
  }
  return paid;
}

// ── Readers, for the panel and for the council ─────────────────────────────

export function controlIn(c: CampaignState, node: number, nation: number): number {
  return c.trade?.states[node]?.control[nation] ?? 0;
}

/** What this crown took out of one node last month. */
export function nodeIncome(c: CampaignState, node: number, nation: number): number {
  return c.trade?.states[node]?.income[nation] ?? 0;
}

/** Every coin this crown took out of trade last month. */
export function tradeIncomeOf(c: CampaignState, nation: number): number {
  let total = 0;
  for (const st of c.trade?.states ?? []) total += st.income[nation] ?? 0;
  return total;
}

/** Nodes this crown holds ground in, ascending. The places it has a say. */
export function nodesHeldBy(c: CampaignState, nation: number): number[] {
  const seen = new Set<number>();
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    const k = tradeNodeOf(c, p);
    if (k >= 0) seen.add(k);
  }
  return [...seen].sort((a, b) => a - b);
}

/**
 * Where a crown may post a merchant: a node it holds ground in, or one directly
 * up- or downstream of such a node.
 *
 * A rule the player can read off the map rather than a range check, and it is
 * the one restriction EU4 does not have that this game wants. A realm of two
 * dozen crowns in which anybody may post a merchant anywhere is a realm where
 * the correct play is always the richest terminal on the board, and sixteen
 * markets would each carry two dozen merchants.
 */
export function canReachNode(c: CampaignState, nation: number, node: number): boolean {
  const t = c.trade;
  if (!t || node < 0 || node >= t.nodes.length) return false;
  const held = nodesHeldBy(c, nation);
  if (held.includes(node)) return true;
  for (const k of held) {
    if (t.nodes[k].outgoing.includes(node)) return true;
    if (t.nodes[node].outgoing.includes(k)) return true;
  }
  return false;
}

/** The nodes value can be steered toward from here. */
export function downstreamOf(c: CampaignState, node: number): number[] {
  return c.trade?.nodes[node]?.outgoing.slice() ?? [];
}

/** Nodes that feed this one. Walked rather than stored; sixteen nodes. */
export function upstreamOf(c: CampaignState, node: number): number[] {
  const out: number[] = [];
  for (const n of c.trade?.nodes ?? []) if (n.outgoing.includes(node)) out.push(n.id);
  return out;
}

/** Who holds the biggest share here, and how much. -1 when nobody holds any. */
export function nodeLeader(c: CampaignState, node: number): { nation: number; share: number } {
  const st = c.trade?.states[node];
  let best = -1;
  let share = 0;
  if (st) {
    for (let n = 0; n < st.control.length; n++) {
      if (st.control[n] > share) {
        share = st.control[n];
        best = n;
      }
    }
  }
  return { nation: best, share };
}

/**
 * What a province is worth to a crown *through trade*, for `ai.ts` to fold into
 * `provinceValue`.
 *
 * Two terms, and the second is the interesting one: what the ground itself puts
 * into its node, plus a share of what taking it would do to the taker's control
 * there. A province in a market you already half own is worth more than the same
 * province in a market you have never traded through — which is how an AI ends
 * up consolidating around a node rather than eating in a straight line.
 */
export function tradeWorthOf(c: CampaignState, nation: number, p: number): number {
  const k = tradeNodeOf(c, p);
  if (k < 0) return 0;
  const def = provinceDef(p);
  const base = def.wealth * TRADE_SHARE;
  const st = c.trade.states[k];
  return base * (1 + (st?.control[nation] ?? 0));
}

/** Total value moving through the whole graph this month. For the panel. */
export function realmTradeValue(c: CampaignState): number {
  let total = 0;
  for (const st of c.trade?.states ?? []) total += st.local;
  return total;
}

/** Sanity for the tests and the sweep: the graph covers the whole realm. */
export function tradeCoverage(c: CampaignState): number {
  let covered = 0;
  for (let p = 0; p < Math.min(PROVINCE_COUNT, c.provinces.length); p++) {
    if (tradeNodeOf(c, p) >= 0) covered++;
  }
  return covered;
}
